import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";
import {
  ShiftCrossReferenceOtShiftOption,
  ShiftCrossReferenceRow,
  ShiftHubOption,
} from "shifts/models";

// Shift Cross-Reference — the Flow port of the legacy "Shift Link Maintenance" screen. Maps
// the railroad's own shift names onto ours for a given location, so their allocation list
// lines up with our yard shifts. Also holds the alert email list for that location.
//
// Not ported: the "Request Allocation List" button, which fires an outbound B2B message
// rather than editing anything on screen. That still lives in the Dashboard.

const ShiftCrossReference = () => {
  // Railroad city names, sourced from the cross-reference table itself — our hub names
  // don't match theirs, so offering hubs here would list locations with no rows behind them.
  const [upLocations, setUpLocations] = useState<string[]>([]);
  const [upLocation, setUpLocation] = useState<string>("");
  const [rows, setRows] = useState<ShiftCrossReferenceRow[]>([]);
  const [knownMappings, setKnownMappings] = useState<Record<string, { shiftName: string; hubId: number }>>({});
  const [otLocations, setOtLocations] = useState<ShiftHubOption[]>([]);
  const [alertEmailList, setAlertEmailList] = useState("");
  // OT shift names are fetched per hub the first time a row needs them.
  const [otShiftsByHub, setOtShiftsByHub] = useState<Record<number, ShiftCrossReferenceOtShiftOption[]>>({});

  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    shiftManagementService
      .getCrossReferenceLocations()
      .then(setUpLocations)
      .catch(() => toast.error("Could not load railroad locations."));
  }, []);

  const loadOtShifts = useCallback(
    async (hubId: number) => {
      if (!hubId || otShiftsByHub[hubId]) return;
      try {
        const shifts = await shiftManagementService.getOtShifts(hubId);
        setOtShiftsByHub((current) => ({ ...current, [hubId]: shifts }));
      } catch {
        // A missing shift list just leaves the dropdown empty; the row is still editable.
      }
    },
    [otShiftsByHub]
  );

  const load = useCallback(async () => {
    if (!upLocation) {
      toast.warn("Pick a railroad location first.");
      return;
    }

    setIsLoading(true);
    setIsDirty(false);
    try {
      const result = await shiftManagementService.getCrossReference(upLocation);
      setRows(result.rows ?? []);
      rememberExistingMappings(result.rows ?? []);
      setOtLocations(result.otLocations ?? []);
      setAlertEmailList(result.alertEmailList ?? "");
      setHasSearched(true);

      // Warm the shift dropdowns for hubs already mapped.
      const hubIds = Array.from(new Set((result.rows ?? []).map((r) => r.otHubId).filter(Boolean)));
      await Promise.all(hubIds.map(loadOtShifts));
    } catch {
      toast.error("Could not load the cross-reference.");
    } finally {
      setIsLoading(false);
    }
  }, [upLocation, loadOtShifts]);

  // The last real mapping we have seen for each row, kept so that unmapping stays
  // undoable. See the comment on the Our Shift Name cell for why this is needed.
  const rememberExistingMappings = (loaded: ShiftCrossReferenceRow[]) => {
    setKnownMappings((current) => {
      const next = { ...current };
      loaded.forEach((row) => {
        // Only remember real mappings; an unmapped row has nothing worth restoring.
        if (row.otShiftName && row.otHubId) {
          next[buildRowKey(row)] = { shiftName: row.otShiftName, hubId: row.otHubId };
        }
      });
      return next;
    });
  };

  const updateRow = (rowKey: string, changes: Partial<ShiftCrossReferenceRow>) => {
    setRows((current) => current.map((row) => (buildRowKey(row) === rowKey ? { ...row, ...changes } : row)));
    setIsDirty(true);
  };

  const handleSave = async () => {
    const recipients = alertEmailList
      .split(/[;,]/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (recipients.length > 0 && recipients.length < 2) {
      toast.error("The alert email list needs at least two recipients.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await shiftManagementService.saveCrossReference({ upLocation, alertEmailList, rows });
      if (result.success) {
        toast.success(result.message || "Cross-reference saved.");
        setIsDirty(false);
        await load();
      } else {
        toast.error(result.message || "Could not save the cross-reference.");
      }
    } catch {
      toast.error("Could not save the cross-reference.");
    } finally {
      setIsSaving(false);
    }
  };

  const columns: GridColDef[] = [
    { field: "upShiftName", headerName: "Railroad Shift Name", flex: 1, minWidth: 220, sortable: false },
    { field: "shiftStart", headerName: "Start", width: 80, sortable: false },
    { field: "shiftEnd", headerName: "End", width: 80, sortable: false },
    {
      field: "otHubId",
      headerName: "Our Location",
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftCrossReferenceRow;
        return (
          <Select
            value={row.otHubId || 0}
            onChange={(e) => {
              const otHubId = Number(e.target.value);
              const location = otLocations.find((l) => l.hubId === otHubId)?.location ?? "";
              // Changing location invalidates the previously chosen shift name.
              updateRow(buildRowKey(row), { otHubId, otLocation: location, otShiftName: "" });
              loadOtShifts(otHubId);
            }}
            variant="standard"
            displayEmpty
            sx={{ width: "100%", fontSize: ".9em" }}
          >
            <MenuItem value={0}>(unmapped)</MenuItem>
            {otLocations.map((location) => (
              <MenuItem key={location.hubId} value={location.hubId}>
                {location.location}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "otShiftName",
      headerName: "Our Shift Name",
      flex: 1.4,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftCrossReferenceRow;
        const options = otShiftsByHub[row.otHubId] ?? [];

        // Names that need their own MenuItem because the master list has no entry for
        // them: whatever the row currently holds, plus the last real mapping we saw.
        //
        // Including the remembered mapping is what makes unmapping reversible. The
        // previous version keyed the fallback off row.otShiftName alone, so it vanished
        // at the exact moment it became useful -- picking "(unmapped)" emptied that
        // field, which removed the only MenuItem carrying the old value, leaving
        // "(unmapped)" as the sole choice and no way back. A legacy mapping like
        // "DYV - PA 2200" is not in the master list, so once dropped it could not be
        // re-entered from this screen at all (there is no free-text option).
        //
        // Guarded on hubId: after the row is pointed at a different hub, the old hub's
        // shift name is meaningless and must not be offered.
        const remembered = knownMappings[buildRowKey(row)];
        const legacyNames = Array.from(
          new Set(
            [row.otShiftName, remembered?.hubId === row.otHubId ? remembered.shiftName : ""].filter(
              (name) => !!name && !options.some((option) => option.shiftName === name)
            )
          )
        );

        return (
          <Select
            value={row.otShiftName || ""}
            onChange={(e) => updateRow(buildRowKey(row), { otShiftName: e.target.value })}
            disabled={!row.otHubId}
            variant="standard"
            displayEmpty
            sx={{ width: "100%", fontSize: ".9em" }}
          >
            <MenuItem value="">(unmapped)</MenuItem>
            {legacyNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
            {options.map((option) => (
              <MenuItem key={option.yardShiftId} value={option.shiftName}>
                {option.shiftName} ({option.timeStart}–{option.timeEnd})
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
  ];

  const unmappedCount = rows.filter((row) => !row.otShiftName).length;

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Shift Cross-Reference
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="xref-location-label">Railroad Location</InputLabel>
            <Select
              labelId="xref-location-label"
              label="Railroad Location"
              value={upLocation}
              onChange={(e) => setUpLocation(e.target.value)}
            >
              {upLocations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" color="info" onClick={load} disabled={isLoading}>
            Get Shifts
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || (!isDirty && !hasSearched)}
            sx={SAVE_BUTTON_SX}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Stack>

        <TextField
          label="Alert Email List"
          value={alertEmailList}
          onChange={(e) => {
            setAlertEmailList(e.target.value);
            setIsDirty(true);
          }}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          helperText="At least two recipients. Separate multiple addresses with a semi-colon or a comma."
        />
      </Paper>

      {hasSearched && unmappedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unmappedCount} shift{unmappedCount === 1 ? " has" : "s have"} no mapping to one of our yard shifts.
        </Alert>
      )}

      <Paper sx={{ height: "calc(100vh - 380px)", minHeight: 340 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => buildRowKey(row as ShiftCrossReferenceRow)}
          loading={isLoading}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
          sx={{ fontSize: ".9em" }}
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {hasSearched ? "No cross-reference rows for this location." : "Pick a railroad location, then Get Shifts."}
              </Stack>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};

// Rows sent by the railroad but not yet saved have no id, so key on the shift identity instead.
const buildRowKey = (row: ShiftCrossReferenceRow): string =>
  row.id > 0 ? `id-${row.id}` : `new-${row.upShiftName}-${row.shiftStart}-${row.shiftEnd}`;

export default ShiftCrossReference;
